import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, ComposedChart, Area, XAxis, YAxis, CartesianGrid, ReferenceLine, LabelList,
} from 'recharts';
import { notificar } from '../utils/notify';
import '../styles/projetosAdvantec.css';

const formatarValor = (valor) => {
  if (!valor && valor !== 0) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(parseFloat(valor));
};

// Paleta de dados: uma cor por departamento (a identidade de cada um segue a
// mesma nos gráficos, na tabela e no modal). Tons calibrados para fundo claro.
const CORES_DEPTO = ['#0b1f4b', '#0891b2', '#15803d', '#d4a72c', '#7c3aed', '#dc2626', '#ea580c', '#64748b'];

const MESES3 = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const rotuloMes = (ym) => {
  const [ano, mes] = String(ym).split('-');
  return `${MESES3[parseInt(mes, 10) - 1]}/${String(ano).slice(2)}`;
};

const pct1 = (n) => String(Number(n || 0).toFixed(1)).replace('.', ',');

// Tick de eixo: valores em R$ mil/M para não estourar a largura do eixo Y
const tickMoeda = (v) => {
  const n = Number(v || 0);
  if (Math.abs(n) >= 1e6) return `R$ ${(n / 1e6).toFixed(1).replace('.', ',')}M`;
  if (Math.abs(n) >= 1e3) return `R$ ${Math.round(n / 1e3)}k`;
  return formatarValor(n);
};

// Rotulo dentro do grafico agrupado: sem "R$" para caber 3 barras por pilar
const valorCurto = (v) => {
  const n = Number(v || 0);
  if (!n) return '';
  const sinal = n < 0 ? '-' : '';
  const a = Math.abs(n);
  if (a >= 1e6) return `${sinal}${(a / 1e6).toFixed(1).replace('.', ',')}M`;
  if (a >= 1e3) return `${sinal}${Math.round(a / 1e3)}k`;
  return `${sinal}${Math.round(a)}`;
};

// Mes em que o periodo padrao comeca (8 = agosto), mesmo criterio do DRE horizontal
const MES_PADRAO_INICIO = 8;
const MESES_ABREV = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const MESES_NOME = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
const pct2 = (n) => Number(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// Cores das empresas no comparativo (ordem em que vêm do backend: a da URL primeiro)
const COR_EMPRESA = ['#0b1f4b', '#0891b2'];

// Cores por PAPEL (receita/despesa/resultado) — as de CORES_DEPTO identificam
// o pilar; aqui o que precisa saltar é o sinal do número.
const COR_RECEITA = '#15803d';
const COR_DESPESA = '#dc2626';
const COR_RESULTADO_POS = '#0891b2';
const COR_RESULTADO_NEG = '#b42318';
const round2js = (v) => Math.round((v + Number.EPSILON) * 100) / 100;

const EIXO = { fontSize: 13, fill: '#667085' };
const GRADE = '#eef0f3';

// ---------- peças de UI (fora do componente: recriá-las a cada render
// remontaria os nós e a animação de entrada dispararia de novo) ----------
const Kpi = ({ i, icone, tone, titulo, valor, corValor, sub, barra }) => (
  <div className="pa-kpi pa-in" style={{ '--i': i }}>
    <div className="pa-kpi-top">
      <span className={`pa-kpi-icon pa-tone-${tone}`}><i className={`fas ${icone}`}></i></span>
      <span className="pa-eyebrow">{titulo}</span>
    </div>
    <div className={`pa-kpi-value pa-num ${corValor || ''}`}>{valor}</div>
    {barra !== undefined && (
      <div className="pa-kpi-bar" aria-hidden="true">
        <span style={{ width: `${Math.max(0, Math.min(barra.pct, 100))}%`, background: barra.cor }}></span>
      </div>
    )}
    {sub && <div className="pa-kpi-sub">{sub}</div>}
  </div>
);

const Seg = ({ valor, onChange, itens, ariaLabel }) => (
  <div className="pa-seg" role="tablist" aria-label={ariaLabel}>
    {itens.map((t) => (
      <button key={t.key} type="button" role="tab" aria-selected={valor === t.key}
        className={valor === t.key ? 'is-on' : ''} title={t.title}
        onClick={() => onChange(t.key)}>
        {t.icone && <i className={`fas ${t.icone}`}></i>}{t.rotulo}
      </button>
    ))}
  </div>
);

const CardHead = ({ icone, tone, titulo, hint, children }) => (
  <div className="pa-card-head">
    <h3 className="pa-card-title">
      <i className={`fas ${icone} pa-tone-${tone}`}></i>
      <span>{titulo}</span>
      {hint && <span className="pa-card-hint">{hint}</span>}
    </h3>
    {children && <div className="pa-card-actions">{children}</div>}
  </div>
);

const Skeleton = () => (
  <>
    <div className="pa-kpis">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="pa-skel-card">
          <div className="pa-skel pa-skel-line" style={{ width: '45%' }}></div>
          <div className="pa-skel pa-skel-num"></div>
          <div className="pa-skel pa-skel-line" style={{ width: '70%' }}></div>
        </div>
      ))}
    </div>
    <div className="pa-grid pa-grid-7-5">
      <div className="pa-card"><div className="pa-skel pa-skel-chart"></div></div>
      <div className="pa-card"><div className="pa-skel pa-skel-chart"></div></div>
    </div>
    <p className="pa-text-muted" style={{ textAlign: 'center', fontSize: 13, marginTop: 8 }}>Carregando análise de departamentos…</p>
  </>
);

const ProjetosAdvantec = () => {
  const { cliente_id } = useParams();
  const navigate = useNavigate();

  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [mensagem, setMensagem] = useState('');
  useEffect(() => { notificar(mensagem); }, [mensagem]);

  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');

  const [lanc, setLanc] = useState({ aberto: false, titulo: '', carregando: false, itens: [], total: 0, categorias: [], aba: 'categorias', cor: '#0b1f4b' });
  const [evolTipo, setEvolTipo] = useState('barras'); // 'barras' | 'linhas'
  const [evolDado, setEvolDado] = useState('despesas'); // 'despesas' | 'receitas'

  // Consolidar empresas do grupo (25 + 27): soma as duas analises em uma so.
  // O ref evita closure desatualizada dentro de carregar/abrirLancamentos.
  const [consolidado, setConsolidado] = useState(false);
  const consolidadoRef = useRef(false);

  // Comparativo por empresa (modelo da planilha): um MES de competencia, bloco
  // consolidado + um por empresa, valor e rateio (% do total do bloco).
  // Independente do filtro de datas da pagina; so existe para clientes com par.
  const [compMes, setCompMes] = useState(MES_PADRAO_INICIO);
  const [compTipo, setCompTipo] = useState('receitas'); // 'receitas' | 'despesas'
  const [comp, setComp] = useState(null);
  const [compCarregando, setCompCarregando] = useState(false);
  const compAno = new Date().getFullYear();

  const carregar = async (ini, fim) => {
    setCarregando(true);
    const token = localStorage.getItem('access');
    try {
      const params = { cliente_id };
      if (ini && fim) { params.data_inicio = ini; params.data_fim = fim; }
      if (consolidadoRef.current) params.consolidar = 1;
      const r = await axios.get(`${process.env.REACT_APP_API_BASE}/api/analise-departamentos/`, {
        params, headers: { Authorization: `Bearer ${token}` }, timeout: 60000,
      });
      if (r.data?.success) {
        setDados(r.data);
        if (r.data.periodo) {
          setDataInicio(r.data.periodo.inicio);
          setDataFim(r.data.periodo.fim);
        }
      } else {
        setDados(null);
        setMensagem(`${r.data?.erro || 'Erro ao carregar análise'}`);
      }
    } catch (e) {
      setDados(null);
      setMensagem('Erro ao conectar com o servidor');
    } finally {
      setCarregando(false);
    }
  };

  // Período padrão: de 1º/MES_PADRAO_INICIO do ano atual até HOJE (só o que já aconteceu)
  const periodoAnoAtual = () => {
    const hoje = new Date();
    const ano = hoje.getFullYear();
    const mes = String(MES_PADRAO_INICIO).padStart(2, '0');
    return [`${ano}-${mes}-01`, hoje.toISOString().slice(0, 10)];
  };

  useEffect(() => {
    if (!cliente_id) return;
    const [ini, fim] = periodoAnoAtual();
    carregar(ini, fim);
    // eslint-disable-next-line
  }, [cliente_id]);

  const abrirLancamentos = async (depto, tipoDado = 'despesas') => {
    const titulo = `${depto.nome} — ${tipoDado} do período`;
    const cor = corDepto[depto.codigo] || '#0b1f4b';
    setLanc({ aberto: true, titulo, carregando: true, itens: [], total: 0, categorias: [], aba: 'categorias', cor, depto, tipoDado });
    const token = localStorage.getItem('access');
    const cfg = { headers: { Authorization: `Bearer ${token}` } };
    // No consolidado o departamento existe nas duas empresas com codigos
    // diferentes: `origens` diz onde buscar. Fora dele, e uma origem so.
    const alvos = (depto.origens && depto.origens.length)
      ? depto.origens
      : [{ cliente_id, codigo: depto.codigo }];
    const nomesEmpresa = {};
    (dados?.clientes || []).forEach((cl) => { nomesEmpresa[String(cl.id)] = cl.nome; });
    try {
      const partes = await Promise.all(alvos.map(async (o) => {
        const params = { cliente_id: o.cliente_id ?? cliente_id, codigo: o.codigo, tipo: tipoDado,
                         data_inicio: dataInicio, data_fim: dataFim };
        const [rCat, rLanc] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_BASE}/api/analise-departamentos-categorias/`, { params, ...cfg }).catch(() => null),
          axios.get(`${process.env.REACT_APP_API_BASE}/api/analise-departamentos-lancamentos/`, { params, ...cfg }).catch(() => null),
        ]);
        // no consolidado toda linha e etiquetada, inclusive a que so existe
        // numa das empresas — e justamente isso que o usuario precisa ver
        const empresa = (depto.origens && depto.origens.length)
          ? (nomesEmpresa[String(o.cliente_id)] || `Cliente ${o.cliente_id}`)
          : null;
        return {
          categorias: rCat?.data?.categorias || [],
          itens: (rLanc?.data?.lancamentos || []).map((l) => (empresa ? { ...l, empresa } : l)),
          total: rLanc?.data?.total ?? rCat?.data?.total ?? 0,
        };
      }));
      // categorias: soma pelo NOME (a mesma categoria existe nas duas empresas)
      const porNome = {};
      partes.forEach((parte) => parte.categorias.forEach((c) => {
        const k = String(c.nome || '(sem categoria)');
        const it = porNome[k] || (porNome[k] = { nome: k, qtd: 0, total: 0 });
        it.qtd += Number(c.qtd || 0);
        it.total += Number(c.total || 0);
      }));
      const categorias = Object.values(porNome).sort((a, b) => b.total - a.total);
      const totalCat = categorias.reduce((acc, c) => acc + c.total, 0);
      categorias.forEach((c) => {
        c.total = Math.round(c.total * 100) / 100;
        c.pct = totalCat > 0 ? Math.round((c.total / totalCat) * 1000) / 10 : 0;
      });
      const itens = [].concat(...partes.map((x) => x.itens))
        .sort((a, b) => String(a.vencimento).localeCompare(String(b.vencimento)));
      setLanc({
        aberto: true, titulo, carregando: false, aba: 'categorias', cor, depto, tipoDado,
        categorias,
        itens,
        total: partes.reduce((acc, x) => acc + Number(x.total || 0), 0),
      });
    } catch (e) {
      setLanc({ aberto: true, titulo, carregando: false, itens: [], total: 0, categorias: [], aba: 'categorias', cor, depto, tipoDado });
    }
  };

  const fecharModal = () => setLanc((p) => ({ ...p, aberto: false }));

  // Esc fecha o modal
  useEffect(() => {
    if (!lanc.aberto) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') fecharModal(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lanc.aberto]);

  useEffect(() => {
    if (!cliente_id || !dados?.consolidavel) return undefined;
    let vivo = true;
    const token = localStorage.getItem('access');
    setCompCarregando(true);
    axios.get(`${process.env.REACT_APP_API_BASE}/api/analise-departamentos-comparativo/`, {
      params: { cliente_id, ano: compAno, mes: compMes, base: 'competencia' },
      headers: { Authorization: `Bearer ${token}` }, timeout: 60000,
    })
      .then((r) => { if (vivo) setComp(r.data?.success ? r.data : { erro: r.data?.erro || 'Falha ao carregar comparativo' }); })
      .catch((e) => { if (vivo) setComp({ erro: e.response?.data?.erro || 'Erro ao carregar comparativo' }); })
      .finally(() => { if (vivo) setCompCarregando(false); });
    return () => { vivo = false; };
    // eslint-disable-next-line
  }, [cliente_id, compMes, dados?.consolidavel]);

  const porDepto = dados?.por_departamento || [];
  const resumo = dados?.resumo || {};
  const corDepto = {};
  porDepto.forEach((d, i) => { corDepto[d.codigo] = CORES_DEPTO[i % CORES_DEPTO.length]; });

  const dadosBarras = porDepto.filter((d) => d.despesas > 0).map((d) => ({
    nome: d.nome, codigo: d.codigo, valor: d.despesas,
  }));
  const dadosDonut = dadosBarras.map((d) => ({ nome: d.nome, valor: d.valor }));

  const dadosBarrasReceitas = porDepto.filter((d) => d.receitas > 0).map((d) => ({
    nome: d.nome, codigo: d.codigo, valor: d.receitas,
  }));

  // Um gráfico só: receita, despesa e resultado de cada pilar lado a lado.
  // "Sem departamento" vai para o fim — é sobra de rateio, não um pilar do negócio.
  const dadosPilar = porDepto
    .filter((d) => d.receitas > 0 || d.despesas > 0)
    .map((d) => ({
      nome: d.nome, codigo: d.codigo,
      receitas: d.receitas, despesas: d.despesas,
      resultado: d.resultado ?? round2js(d.receitas - d.despesas),
    }))
    .sort((a, b) => (a.codigo === '__sem__' ? 1 : b.codigo === '__sem__' ? -1
                     : (b.receitas + b.despesas) - (a.receitas + a.despesas)));
  const dadosDonutReceitas = dadosBarrasReceitas.map((d) => ({ nome: d.nome, valor: d.valor }));

  const evolucao = (dados?.evolucao_mensal || []).map((l) => ({ ...l, nome: rotuloMes(l.mes) }));
  const evolucaoReceitas = (dados?.evolucao_mensal_receitas || []).map((l) => ({ ...l, nome: rotuloMes(l.mes) }));
  const temReceitas = porDepto.some((d) => d.receitas > 0);
  const dadosEvol = evolDado === 'receitas' ? evolucaoReceitas : evolucao;
  const deptosEvol = porDepto.filter((d) => (evolDado === 'receitas' ? d.receitas > 0 : d.despesas > 0));

  const fmtD = (d) => (d ? new Date(String(d).slice(0, 10) + 'T00:00:00').toLocaleDateString('pt-BR') : '—');

  const nomeEmpresa = dados?.consolidado
    ? (dados.clientes || []).map((c) => c.nome).join(' + ')
    : null;

  // Tooltip escuro (nome + valor + % do total) para o donut e as barras
  // por departamento — fábrica: recebe a base do percentual (despesas ou receitas)
  const fazTooltipDepto = (totalBase) => ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const p = payload[0];
    const nome = p.name || p.payload?.nome;
    const valor = p.value ?? p.payload?.valor ?? 0;
    const pct = totalBase > 0 ? (valor / totalBase) * 100 : null;
    const cor = p.payload?.fill || p.color || p.fill || (p.payload?.codigo ? corDepto[p.payload.codigo] : undefined);
    return (
      <div className="pa-tooltip">
        <div className="pa-tooltip-title">{nome}</div>
        <div className="pa-tooltip-row">
          <span><span className="pa-dot" style={{ background: cor }}></span>Valor</span>
          <b>{formatarValor(valor)}</b>
        </div>
        {pct !== null && (
          <div className="pa-tooltip-total">
            <span>Participação</span>
            <b>{pct1(pct)}%</b>
          </div>
        )}
      </div>
    );
  };
  // Tooltip do gráfico único: os três números do pilar + a margem dele
  const tooltipPilar = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0].payload || {};
    const margem = d.receitas > 0 ? (d.resultado / d.receitas) * 100 : null;
    return (
      <div className="pa-tooltip">
        <div className="pa-tooltip-title">{d.nome}</div>
        <div className="pa-tooltip-row">
          <span><span className="pa-dot" style={{ background: COR_RECEITA }}></span>Receita</span>
          <b>{formatarValor(d.receitas)}</b>
        </div>
        <div className="pa-tooltip-row">
          <span><span className="pa-dot" style={{ background: COR_DESPESA }}></span>Despesa</span>
          <b>{formatarValor(d.despesas)}</b>
        </div>
        <div className="pa-tooltip-total">
          <span>Resultado{margem !== null ? ` · margem ${pct1(margem)}%` : ''}</span>
          <b className={d.resultado >= 0 ? 'pa-text-green' : 'pa-text-red'}>{formatarValor(d.resultado)}</b>
        </div>
      </div>
    );
  };
  const tooltipDepto = fazTooltipDepto(resumo.total_despesas);
  const tooltipDeptoReceitas = fazTooltipDepto(resumo.total_receitas);

  const tooltipEscuro = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const itens = payload.filter((p) => (p.value || 0) > 0);
    if (!itens.length) return null;
    const total = itens.reduce((s, p) => s + (p.value || 0), 0);
    return (
      <div className="pa-tooltip">
        <div className="pa-tooltip-title">{label}</div>
        {itens.map((p, i) => (
          <div key={i} className="pa-tooltip-row">
            <span style={{ display: 'flex', alignItems: 'center' }}>
              <span className="pa-dot" style={{ background: p.color || p.fill }}></span>{p.name}
            </span>
            <b>{formatarValor(p.value)}</b>
          </div>
        ))}
        {itens.length > 1 && (
          <div className="pa-tooltip-total">
            <span>Total do mês</span>
            <b>{formatarValor(total)}</b>
          </div>
        )}
      </div>
    );
  };

  const legendaPct = (lista, base) => (v) => {
    const item = lista.find((x) => x.nome === v);
    const pct = item && base > 0 ? (item.valor / base) * 100 : null;
    return <span>{v}{pct !== null ? ` · ${pct1(pct)}%` : ''}</span>;
  };

  const coberturaOk = (resumo.cobertura_pct ?? 0) >= 80;
  const resultadoOk = (resumo.resultado ?? 0) >= 0;

  return (
    <div className="pa-page">
      {/* ---------- cabeçalho ---------- */}
      <header className="pa-header pa-in" style={{ '--i': 0 }}>
        <div>
          <div className="pa-eyebrow">Projetos · Análise de departamentos</div>
          <h1 className="pa-title">{nomeEmpresa || 'Despesas e receitas por departamento'}</h1>
          <p className="pa-subtitle">
            {nomeEmpresa
              ? 'Visão consolidada do grupo — departamentos com o mesmo nome somados nas duas empresas.'
              : 'Distribuição do rateio de departamentos (Omie) no período selecionado.'}
          </p>
        </div>
        <div className="pa-header-right">
          {dados?.consolidado && (
            <span className="pa-chip pa-chip-gold"><i className="fas fa-layer-group"></i>Grupo consolidado</span>
          )}
          {dados?.periodo && (
            <span className="pa-chip pa-chip-outline">
              <i className="fas fa-calendar"></i>{fmtD(dados.periodo.inicio)} — {fmtD(dados.periodo.fim)}
            </span>
          )}
          <button type="button" className="pa-btn pa-btn-ghost" onClick={() => navigate(`/dashboard-cliente/${cliente_id}`)}>
            <i className="fas fa-arrow-left"></i>Dashboard
          </button>
        </div>
      </header>

      {/* ---------- filtros ---------- */}
      <section className="pa-toolbar pa-in" style={{ '--i': 1 }} aria-label="Filtros">
        <div className="pa-field">
          <label className="pa-eyebrow" htmlFor="pa-ini">Data início</label>
          <input id="pa-ini" type="date" className="pa-input" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} />
        </div>
        <div className="pa-field">
          <label className="pa-eyebrow" htmlFor="pa-fim">Data fim</label>
          <input id="pa-fim" type="date" className="pa-input" value={dataFim} onChange={(e) => setDataFim(e.target.value)} />
        </div>
        <div className="pa-toolbar-actions">
          <button type="button" className="pa-btn pa-btn-primary" disabled={!dataInicio || !dataFim || carregando}
            onClick={() => carregar(dataInicio, dataFim)}>
            <i className="fas fa-magnifying-glass"></i>Filtrar
          </button>
          <button type="button" className="pa-btn pa-btn-ghost" disabled={carregando}
            onClick={() => { const [ini, fim] = periodoAnoAtual(); carregar(ini, fim); }}>
            <i className="fas fa-rotate-left"></i>Resetar
          </button>
        </div>
        {(dados?.consolidavel || consolidado) && (
          <div className="pa-toolbar-right">
            <button type="button" className={`pa-btn pa-btn-toggle ${consolidado ? 'is-on' : ''}`}
              aria-pressed={consolidado}
              title="Somar esta análise com a da outra empresa do grupo (Advantec + Advwg)"
              onClick={() => {
                const v = !consolidado;
                setConsolidado(v);
                consolidadoRef.current = v;
                fecharModal();
                carregar(dataInicio, dataFim);
              }}>
              <i className="fas fa-layer-group"></i>{consolidado ? 'Consolidado' : 'Consolidar empresas'}
            </button>
          </div>
        )}
      </section>

      {carregando ? (
        <Skeleton />
      ) : !dados || dados.vazio ? (
        <div className="pa-empty pa-in" style={{ '--i': 2 }}>
          <div className="pa-empty-icon"><i className="fas fa-sitemap"></i></div>
          <h4>Nenhum rateio de departamento no período</h4>
          <p>Ajuste as datas acima ou verifique se os títulos foram rateados na Omie.</p>
        </div>
      ) : (
        <>
          {/* ---------- KPIs de despesas ---------- */}
          <div className="pa-kpis">
            <Kpi i={2} icone="fa-coins" tone="navy" titulo="Total despesas"
              valor={formatarValor(resumo.total_despesas)}
              sub={`${resumo.qtd_titulos} título(s) rateado(s) no período`} />
            <Kpi i={3} icone="fa-circle-check" tone="green" titulo="Já pago"
              valor={formatarValor(resumo.total_despesas_pagas)} corValor="pa-text-green"
              barra={{ pct: resumo.total_despesas > 0 ? (resumo.total_despesas_pagas / resumo.total_despesas) * 100 : 0, cor: '#15803d' }}
              sub={resumo.total_despesas > 0
                ? `${pct1((resumo.total_despesas_pagas / resumo.total_despesas) * 100)}% das despesas já quitadas`
                : 'Despesas quitadas no período'} />
            <Kpi i={4} icone="fa-clock" tone="cyan" titulo="A pagar"
              valor={formatarValor(resumo.total_despesas_abertas)} corValor="pa-text-cyan"
              sub="Previstas e ainda em aberto no período" />
            <Kpi i={5} icone="fa-percent" tone={coberturaOk ? 'green' : 'red'} titulo="Cobertura do rateio"
              valor={`${pct1(resumo.cobertura_pct ?? 0)}%`} corValor={coberturaOk ? 'pa-text-green' : 'pa-text-red'}
              barra={{ pct: resumo.cobertura_pct ?? 0, cor: coberturaOk ? '#15803d' : '#b42318' }}
              sub={`${resumo.titulos_sem_departamento} título(s) sem departamento`} />
          </div>

          {/* ---------- KPIs de receita ---------- */}
          {temReceitas && (
            <div className="pa-kpis">
              <Kpi i={6} icone="fa-hand-holding-dollar" tone="green" titulo="Total receitas"
                valor={formatarValor(resumo.total_receitas)} corValor="pa-text-green"
                sub={resumo.receitas_fonte === 'hibrido'
                  ? 'Rateio manual do título; sem rateio, pelo nome da categoria'
                  : resumo.receitas_fonte === 'categorias'
                    ? 'Mapeadas pelo nome das categorias (1.1.x)'
                    : 'Rateio de departamentos'} />
              <Kpi i={7} icone="fa-circle-check" tone="navy" titulo="Já recebido"
                valor={formatarValor(resumo.total_receitas_recebidas)}
                barra={{ pct: resumo.total_receitas > 0 ? (resumo.total_receitas_recebidas / resumo.total_receitas) * 100 : 0, cor: '#0b1f4b' }}
                sub="Receitas recebidas no período" />
              <Kpi i={8} icone="fa-scale-balanced" tone={resultadoOk ? 'green' : 'red'} titulo="Resultado"
                valor={formatarValor(resumo.resultado)} corValor={resultadoOk ? 'pa-text-green' : 'pa-text-red'}
                sub={resumo.total_receitas > 0
                  ? `Receitas − despesas · margem de ${pct1((resumo.resultado / resumo.total_receitas) * 100)}%`
                  : 'Receitas − despesas'} />
              <Kpi i={9} icone="fa-triangle-exclamation" tone="orange" titulo="Receitas sem departamento"
                valor={formatarValor(resumo.receitas_nao_mapeadas_total)} corValor="pa-text-orange"
                sub="Categorias sem nome de departamento (ex.: Revenda, Comissões)" />
            </div>
          )}

          {temReceitas && (resumo.receitas_nao_mapeadas || []).length > 0 && (
            <div className="pa-note pa-in" style={{ '--i': 10 }}>
              <i className="fas fa-circle-info"></i>
              <div>
                <strong>Receitas fora do de-para</strong> — a categoria não corresponde a nenhum departamento:{' '}
                {resumo.receitas_nao_mapeadas.map((x, i) => (
                  <span key={x.categoria}>
                    {i > 0 && <span className="pa-sep">·</span>}
                    {x.categoria} <span className="pa-tab-num">({formatarValor(x.total)})</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* ---------- pilar: receita x despesa x resultado num só gráfico ---------- */}
          <div className="pa-grid">
            <div className="pa-card pa-in" style={{ '--i': 6 }}>
              <CardHead icone="fa-chart-column" tone="navy" titulo="Receita, despesa e resultado por pilar"
                hint="clique na barra de receita ou de despesa para detalhar">
                {(resumo.receitas_fonte === 'categorias' || resumo.receitas_fonte === 'hibrido') && (
                  <span className="pa-chip pa-chip-green">
                    <i className="fas fa-tags"></i>
                    {resumo.receitas_fonte === 'hibrido' ? 'Rateio manual + nome da categoria' : 'Pelo nome da categoria'}
                  </span>
                )}
              </CardHead>
              <div className="pa-card-body">
                <ResponsiveContainer width="100%" height={470}>
                  <BarChart data={dadosPilar} margin={{ top: 34, right: 16, left: 4, bottom: 8 }}
                    barCategoryGap="24%" barGap={3}>
                    <CartesianGrid strokeDasharray="3 3" stroke={GRADE} vertical={false} />
                    <XAxis dataKey="nome" tick={EIXO} interval={0} axisLine={false} tickLine={false}
                      tickFormatter={(v) => (String(v).length > 22 ? `${String(v).slice(0, 21)}…` : v)} />
                    <YAxis tickFormatter={tickMoeda} tick={EIXO} axisLine={false} tickLine={false} width={82} />
                    <Tooltip content={tooltipPilar} cursor={{ fill: 'rgba(11,31,75,0.04)' }} />
                    <Legend iconType="circle" iconSize={11} />
                    {/* o zero precisa ficar visível: o resultado desce abaixo dele */}
                    <ReferenceLine y={0} stroke="#c8cdd6" />
                    <Bar dataKey="receitas" name="Receita" fill={COR_RECEITA} radius={[5, 5, 0, 0]} maxBarSize={54}
                      style={{ cursor: 'pointer' }}
                      onClick={(d) => {
                        const pl = d && d.payload ? d.payload : d;
                        const depto = porDepto.find((x) => x.codigo === pl.codigo);
                        if (depto) abrirLancamentos(depto, 'receitas');
                      }}>
                      <LabelList dataKey="receitas" position="top" formatter={valorCurto}
                        style={{ fontSize: 11, fill: COR_RECEITA, fontWeight: 700 }} />
                    </Bar>
                    <Bar dataKey="despesas" name="Despesa" fill={COR_DESPESA} radius={[5, 5, 0, 0]} maxBarSize={54}
                      style={{ cursor: 'pointer' }}
                      onClick={(d) => {
                        const pl = d && d.payload ? d.payload : d;
                        const depto = porDepto.find((x) => x.codigo === pl.codigo);
                        if (depto) abrirLancamentos(depto);
                      }}>
                      <LabelList dataKey="despesas" position="top" formatter={valorCurto}
                        style={{ fontSize: 11, fill: COR_DESPESA, fontWeight: 700 }} />
                    </Bar>
                    <Bar dataKey="resultado" name="Resultado" radius={[5, 5, 5, 5]} maxBarSize={54}>
                      {dadosPilar.map((d) => (
                        <Cell key={d.codigo} fill={d.resultado >= 0 ? COR_RESULTADO_POS : COR_RESULTADO_NEG} />
                      ))}
                      <LabelList dataKey="resultado" position="top" formatter={valorCurto}
                        style={{ fontSize: 11, fill: '#344054', fontWeight: 800 }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* ---------- distribuição: um donut de cada lado ---------- */}
          <div className={`pa-grid ${temReceitas ? 'pa-grid-6-6' : ''}`}>
            <div className="pa-card pa-in" style={{ '--i': 7 }}>
              <CardHead icone="fa-chart-pie" tone="navy" titulo="Distribuição das despesas" />
              <div className="pa-card-body pa-donut">
                <ResponsiveContainer width="100%" height={440}>
                  <PieChart>
                    <Pie data={dadosDonut} dataKey="valor" nameKey="nome" innerRadius="56%" outerRadius="86%"
                      paddingAngle={2} strokeWidth={2} stroke="#fff">
                      {dadosBarras.map((d) => (
                        <Cell key={d.codigo} fill={corDepto[d.codigo]} />
                      ))}
                    </Pie>
                    <Tooltip content={tooltipDepto} />
                    <Legend iconType="circle" iconSize={11} formatter={legendaPct(dadosDonut, resumo.total_despesas)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {temReceitas && dadosBarrasReceitas.length > 0 && (
              <div className="pa-card pa-in" style={{ '--i': 8 }}>
                <CardHead icone="fa-chart-pie" tone="green" titulo="Distribuição das receitas" />
                <div className="pa-card-body pa-donut">
                  <ResponsiveContainer width="100%" height={440}>
                    <PieChart>
                      <Pie data={dadosDonutReceitas} dataKey="valor" nameKey="nome" innerRadius="56%" outerRadius="86%"
                        paddingAngle={2} strokeWidth={2} stroke="#fff">
                        {dadosBarrasReceitas.map((d) => (
                          <Cell key={d.codigo} fill={corDepto[d.codigo]} />
                        ))}
                      </Pie>
                      <Tooltip content={tooltipDeptoReceitas} />
                      <Legend iconType="circle" iconSize={11} formatter={legendaPct(dadosDonutReceitas, resumo.total_receitas)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>

          {/* ---------- evolução mensal ---------- */}
          <div className="pa-grid">
            <div className="pa-card pa-in" style={{ '--i': 10 }}>
              <CardHead icone="fa-chart-line" tone={evolDado === 'receitas' ? 'green' : 'navy'}
                titulo={`Evolução mensal das ${evolDado === 'receitas' ? 'receitas' : 'despesas'}`}>
                {temReceitas && (
                  <Seg valor={evolDado} onChange={setEvolDado} ariaLabel="Tipo de dado"
                    itens={[
                      { key: 'despesas', icone: 'fa-coins', rotulo: 'Despesas', title: 'Ver evolução de despesas' },
                      { key: 'receitas', icone: 'fa-hand-holding-dollar', rotulo: 'Receitas', title: 'Ver evolução de receitas' },
                    ]} />
                )}
                <Seg valor={evolTipo} onChange={setEvolTipo} ariaLabel="Tipo de gráfico"
                  itens={[
                    { key: 'barras', icone: 'fa-chart-column', rotulo: 'Barras', title: 'Ver em barras' },
                    { key: 'linhas', icone: 'fa-chart-line', rotulo: 'Linhas', title: 'Ver em linhas' },
                  ]} />
              </CardHead>
              <div className="pa-card-body">
                <ResponsiveContainer width="100%" height={440}>
                  {evolTipo === 'linhas' ? (
                    <ComposedChart data={dadosEvol} margin={{ top: 16, right: 12, left: 4, bottom: 4 }}>
                      <defs>
                        {deptosEvol.map((d) => (
                          <linearGradient key={d.codigo} id={`depGrad-${d.codigo}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={corDepto[d.codigo]} stopOpacity={0.22} />
                            <stop offset="70%" stopColor={corDepto[d.codigo]} stopOpacity={0.05} />
                            <stop offset="100%" stopColor={corDepto[d.codigo]} stopOpacity={0} />
                          </linearGradient>
                        ))}
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={GRADE} vertical={false} />
                      <XAxis dataKey="nome" tick={EIXO} axisLine={false} tickLine={false} />
                      <YAxis domain={[0, 'auto']} tickFormatter={tickMoeda} tick={EIXO} axisLine={false} tickLine={false} width={78} />
                      <Tooltip content={tooltipEscuro} cursor={{ stroke: '#cbd5e1', strokeDasharray: '4 4' }} />
                      <Legend iconType="circle" iconSize={11} />
                      {deptosEvol.map((d) => (
                        <Area key={d.codigo} type="monotone" dataKey={d.codigo} name={d.nome}
                          stroke={corDepto[d.codigo]} strokeWidth={3} fill={`url(#depGrad-${d.codigo})`} connectNulls
                          dot={{ r: 4.5, fill: corDepto[d.codigo], stroke: '#fff', strokeWidth: 2 }}
                          activeDot={{ r: 6, fill: corDepto[d.codigo], stroke: '#fff', strokeWidth: 2.5 }} />
                      ))}
                    </ComposedChart>
                  ) : (
                    <BarChart data={dadosEvol} margin={{ top: 16, right: 12, left: 4, bottom: 4 }} barCategoryGap="32%">
                      <CartesianGrid strokeDasharray="3 3" stroke={GRADE} vertical={false} />
                      <XAxis dataKey="nome" tick={EIXO} axisLine={false} tickLine={false} />
                      <YAxis domain={[0, 'auto']} tickFormatter={tickMoeda} tick={EIXO} axisLine={false} tickLine={false} width={78} />
                      <Tooltip content={tooltipEscuro} cursor={{ fill: 'rgba(11,31,75,0.05)' }} />
                      <Legend iconType="circle" iconSize={11} />
                      {deptosEvol.map((d, idx, arr) => (
                        <Bar key={d.codigo} dataKey={d.codigo} name={d.nome} stackId="dep" fill={corDepto[d.codigo]}
                          radius={idx === arr.length - 1 ? [6, 6, 0, 0] : [0, 0, 0, 0]} maxBarSize={72} />
                      ))}
                    </BarChart>
                  )}
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* ---------- comparativo por empresa (modelo da planilha) ---------- */}
          {dados?.consolidavel && (
            <div className="pa-grid">
              <div className="pa-card pa-in" style={{ '--i': 11 }}>
                <CardHead icone="fa-table-columns" tone="gold"
                  titulo={`Comparativo por empresa — ${compTipo} de ${MESES_NOME[compMes - 1]}/${compAno}`}
                  hint={compTipo === 'receitas' ? 'competência = data de emissão' : 'por vencimento'}>
                  <Seg valor={compTipo} onChange={setCompTipo} ariaLabel="Tipo de dado"
                    itens={[
                      { key: 'receitas', icone: 'fa-hand-holding-dollar', rotulo: 'Receitas' },
                      { key: 'despesas', icone: 'fa-coins', rotulo: 'Despesas' },
                    ]} />
                </CardHead>
                <div className="pa-card-sub">
                  <span className="pa-eyebrow">Mês</span>
                  <div className="pa-months" role="tablist" aria-label="Mês de competência">
                    {MESES_ABREV.map((m, i) => (
                      <button key={m} type="button" role="tab" aria-selected={compMes === i + 1}
                        className={compMes === i + 1 ? 'is-on' : ''} onClick={() => setCompMes(i + 1)}>{m}</button>
                    ))}
                  </div>
                </div>
                <div className="pa-card-body pa-card-body-table">
                  {compCarregando ? (
                    <div style={{ padding: '48px 0' }}><div className="pa-spinner"></div></div>
                  ) : !comp || comp.erro ? (
                    <div className="pa-modal-empty"><i className="fas fa-triangle-exclamation"></i>{comp?.erro || 'Sem dados'}</div>
                  ) : (comp.linhas || []).length === 0 ? (
                    <div className="pa-modal-empty"><i className="fas fa-inbox"></i>Nenhum departamento com {compTipo} em {MESES_NOME[compMes - 1]}</div>
                  ) : (() => {
                    const empresas = (comp.clientes || []).map((c, i) => ({ key: String(c.id), nome: c.nome, cor: COR_EMPRESA[i % COR_EMPRESA.length] }));
                    const blocos = [{ key: 'consolidado', nome: 'Consolidado', cor: '#d4a72c', cons: true }].concat(empresas);
                    const val = (linha, key) => (key === 'consolidado' ? linha.consolidado[compTipo] : linha.empresas[key][compTipo]);
                    const tot = (key) => comp.totais?.[key]?.[compTipo] ?? 0;
                    const rotulo = compTipo === 'receitas' ? 'Receitas' : 'Despesas';
                    return (
                      <div className="pa-table-wrap">
                        <table className="pa-table pa-table-comp">
                          <thead>
                            <tr>
                              <th className="c-depto">Departamento</th>
                              {blocos.map((b) => (
                                <th key={b.key} className={`c-bloco ${b.cons ? 'is-cons' : ''}`}>
                                  <span className="pa-comp-head">
                                    <span className="pa-dot" style={{ background: b.cor }}></span>{b.nome}
                                  </span>
                                  <span className="pa-comp-head-sub">{rotulo} · rateio no bloco</span>
                                </th>
                              ))}
                              {empresas.length === 2 && (
                                <th className="c-split">
                                  <span className="pa-comp-head">Divisão entre empresas</span>
                                  <span className="pa-comp-head-sub">quanto de cada uma no consolidado</span>
                                </th>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {comp.linhas.map((l) => {
                              const cons = val(l, 'consolidado');
                              return (
                                <tr key={l.codigo}>
                                  <td className="c-depto t-strong">{l.nome}</td>
                                  {blocos.map((b) => {
                                    const v = val(l, b.key);
                                    const r = l.rateio?.[compTipo]?.[b.key] ?? 0;
                                    return (
                                      <td key={b.key} className={`c-bloco ${b.cons ? 'is-cons' : ''}`}>
                                        {v ? (
                                          <div className="pa-comp-cell">
                                            <span className={`pa-comp-val pa-tab-num ${b.cons ? 't-strong' : ''}`}>{formatarValor(v)}</span>
                                            <span className="pa-comp-share">
                                              <span className="pa-comp-track"><span style={{ width: `${Math.min(r, 100)}%`, background: b.cor }}></span></span>
                                              <span className="pa-comp-pct">{pct2(r)}%</span>
                                            </span>
                                          </div>
                                        ) : (
                                          <span className="pa-comp-empty">—</span>
                                        )}
                                      </td>
                                    );
                                  })}
                                  {empresas.length === 2 && (
                                    <td className="c-split">
                                      {cons > 0 ? (
                                        <div className="pa-split">
                                          <span className="pa-split-bar">
                                            {empresas.map((e) => {
                                              const parte = (val(l, e.key) / cons) * 100;
                                              return parte > 0 ? <span key={e.key} style={{ width: `${parte}%`, background: e.cor }} title={`${e.nome}: ${pct2(parte)}%`}></span> : null;
                                            })}
                                          </span>
                                          <span className="pa-split-lbl">
                                            {empresas.map((e, i) => (
                                              <span key={e.key} style={{ color: e.cor }}>{i > 0 && <span className="pa-split-sep">·</span>}{Math.round((val(l, e.key) / cons) * 100)}%</span>
                                            ))}
                                          </span>
                                        </div>
                                      ) : <span className="pa-comp-empty">—</span>}
                                    </td>
                                  )}
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot>
                            <tr>
                              <th className="c-depto">Total</th>
                              {blocos.map((b) => (
                                <th key={b.key} className={`c-bloco ${b.cons ? 'is-cons' : ''}`}>
                                  <div className="pa-comp-cell">
                                    <span className="pa-comp-val pa-tab-num t-strong">{formatarValor(tot(b.key))}</span>
                                    {tot(b.key) > 0 && (
                                      <span className="pa-comp-share">
                                        <span className="pa-comp-track"><span style={{ width: '100%', background: b.cor, opacity: 0.35 }}></span></span>
                                        <span className="pa-comp-pct">100%</span>
                                      </span>
                                    )}
                                  </div>
                                </th>
                              ))}
                              {empresas.length === 2 && (
                                <th className="c-split">
                                  {tot('consolidado') > 0 && (
                                    <div className="pa-split">
                                      <span className="pa-split-bar">
                                        {empresas.map((e) => {
                                          const parte = (tot(e.key) / tot('consolidado')) * 100;
                                          return parte > 0 ? <span key={e.key} style={{ width: `${parte}%`, background: e.cor }}></span> : null;
                                        })}
                                      </span>
                                      <span className="pa-split-lbl">
                                        {empresas.map((e, i) => (
                                          <span key={e.key} style={{ color: e.cor }}>{i > 0 && <span className="pa-split-sep">·</span>}{Math.round((tot(e.key) / tot('consolidado')) * 100)}%</span>
                                        ))}
                                      </span>
                                    </div>
                                  )}
                                </th>
                              )}
                            </tr>
                          </tfoot>
                        </table>
                        <div className="pa-table-note">
                          <i className="fas fa-circle-info"></i>
                          Consolidado = soma simples das duas empresas. A barra de cada bloco é a participação do departamento no total daquele bloco;
                          a coluna Divisão mostra quanto de cada empresa compõe o valor consolidado da linha.
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}

          {/* ---------- tabela ---------- */}
          <div className="pa-grid">
            <div className="pa-card pa-in" style={{ '--i': 12 }}>
              <CardHead icone="fa-table-list" tone="navy" titulo="Detalhamento por departamento" hint="clique na linha para ver os lançamentos" />
              <div className="pa-card-body pa-card-body-table">
                <div className="pa-table-wrap">
                  <table className="pa-table">
                    <thead>
                      <tr>
                        <th>Departamento</th>
                        <th className="t-center">Títulos</th>
                        <th className="t-end">Pago</th>
                        <th className="t-end">A pagar</th>
                        <th className="t-end">Total despesas</th>
                        {temReceitas && <th className="t-end">Receitas</th>}
                        {temReceitas && <th className="t-end">Resultado</th>}
                        <th className="t-end" style={{ width: '18%' }}>% das despesas</th>
                      </tr>
                    </thead>
                    <tbody>
                      {porDepto.map((d) => (
                        <tr key={d.codigo} className="is-click" title="Ver lançamentos" onClick={() => abrirLancamentos(d)}>
                          <td className="t-strong">
                            <span className="pa-dot" style={{ background: corDepto[d.codigo] }}></span>
                            {d.nome}
                          </td>
                          <td className="t-center"><span className="pa-count">{d.qtd_despesas + d.qtd_receitas}</span></td>
                          <td className="t-end pa-tab-num pa-text-green">{formatarValor(d.despesas_pagas)}</td>
                          <td className="t-end pa-tab-num pa-text-cyan">{formatarValor(d.despesas - d.despesas_pagas)}</td>
                          <td className="t-end pa-tab-num t-strong">{formatarValor(d.despesas)}</td>
                          {temReceitas && <td className="t-end pa-tab-num pa-text-green">{formatarValor(d.receitas)}</td>}
                          {temReceitas && (
                            <td className={`t-end pa-tab-num t-strong ${(d.resultado ?? 0) >= 0 ? 'pa-text-green' : 'pa-text-red'}`}>
                              {formatarValor(d.resultado)}
                              {d.receitas > 0 && (
                                <small className="t-sub">margem {pct1((d.resultado / d.receitas) * 100)}%</small>
                              )}
                            </td>
                          )}
                          <td>
                            <div className="pa-share">
                              <div className="pa-share-track" aria-hidden="true">
                                <span style={{ width: `${Math.min(d.pct_despesas, 100)}%`, background: corDepto[d.codigo] }}></span>
                              </div>
                              <span className="pa-share-val">{pct1(d.pct_despesas)}%</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <th>Total</th>
                        <th className="t-center"><span className="pa-count">{resumo.qtd_titulos}</span></th>
                        <th className="t-end pa-tab-num pa-text-green">{formatarValor(resumo.total_despesas_pagas)}</th>
                        <th className="t-end pa-tab-num pa-text-cyan">{formatarValor(resumo.total_despesas_abertas)}</th>
                        <th className="t-end pa-tab-num">{formatarValor(resumo.total_despesas)}</th>
                        {temReceitas && <th className="t-end pa-tab-num pa-text-green">{formatarValor(resumo.total_receitas)}</th>}
                        {temReceitas && (
                          <th className={`t-end pa-tab-num ${resultadoOk ? 'pa-text-green' : 'pa-text-red'}`}>
                            {formatarValor(resumo.resultado)}
                          </th>
                        )}
                        <th></th>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ---------- modal de lançamentos ---------- */}
      {lanc.aberto && (
        <div className="pa-modal" onClick={fecharModal} role="presentation">
          <div className="pa-modal-card" role="dialog" aria-modal="true" aria-labelledby="pa-modal-title" onClick={(e) => e.stopPropagation()}>
            <div className="pa-modal-head">
              <div>
                <div className="pa-eyebrow" style={{ color: lanc.cor }}>
                  <span className="pa-dot" style={{ background: lanc.cor, width: 8, height: 8, borderRadius: '50%', display: 'inline-block', marginRight: 8, verticalAlign: 'middle' }}></span>
                  {lanc.tipoDado === 'receitas' ? 'Receitas' : 'Despesas'} do período
                </div>
                <h2 id="pa-modal-title" className="pa-modal-title">{lanc.depto?.nome || lanc.titulo}</h2>
              </div>
              <button type="button" className="pa-icon-btn" aria-label="Fechar" onClick={fecharModal}>
                <i className="fas fa-xmark"></i>
              </button>
            </div>

            <div className="pa-modal-tools">
              <Seg valor={lanc.aba} onChange={(k) => setLanc((p) => ({ ...p, aba: k }))} ariaLabel="Visão"
                itens={[
                  { key: 'categorias', icone: 'fa-layer-group', rotulo: 'Por categoria' },
                  { key: 'lancamentos', icone: 'fa-receipt', rotulo: `Lançamentos (${lanc.itens.length})` },
                ]} />
              <div className="pa-modal-total">
                {temReceitas && lanc.depto && (
                  <Seg valor={lanc.tipoDado || 'despesas'} ariaLabel="Tipo de dado"
                    onChange={(k) => (lanc.tipoDado || 'despesas') !== k && abrirLancamentos(lanc.depto, k)}
                    itens={[
                      { key: 'despesas', icone: 'fa-coins', rotulo: 'Despesas' },
                      { key: 'receitas', icone: 'fa-hand-holding-dollar', rotulo: 'Receitas' },
                    ]} />
                )}
                <span className="pa-num">{formatarValor(lanc.total)}</span>
              </div>
            </div>

            <div className="pa-modal-body">
              {lanc.carregando ? (
                <div style={{ padding: '56px 0' }}><div className="pa-spinner"></div></div>
              ) : lanc.aba === 'categorias' ? (
                lanc.categorias.length > 0 ? (
                  <table className="pa-table pa-table-sm">
                    <thead>
                      <tr>
                        <th>Categoria</th>
                        <th className="t-center" style={{ width: '11%' }}>Títulos</th>
                        <th className="t-end" style={{ width: '20%' }}>Valor</th>
                        <th className="t-end" style={{ width: '30%' }}>% do departamento</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lanc.categorias.map((c, i) => (
                        <tr key={i}>
                          <td className="t-strong">{c.nome}</td>
                          <td className="t-center"><span className="pa-count">{c.qtd}</span></td>
                          <td className="t-end pa-tab-num t-strong">{formatarValor(c.total)}</td>
                          <td>
                            <div className="pa-share">
                              <div className="pa-share-track" style={{ flex: '0 0 130px' }} aria-hidden="true">
                                <span style={{ width: `${Math.min(c.pct, 100)}%`, background: lanc.cor }}></span>
                              </div>
                              <span className="pa-share-val">{pct1(c.pct)}%</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="pa-modal-empty"><i className="fas fa-layer-group"></i>Nenhuma categoria encontrada</div>
                )
              ) : lanc.itens.length > 0 ? (
                <table className="pa-table pa-table-sm">
                  <thead>
                    <tr>
                      <th style={{ width: '14%' }}>Vencimento</th>
                      {dados?.consolidado && <th style={{ width: '16%' }}>Empresa</th>}
                      <th>Fornecedor</th>
                      {/* com rateio de categoria o mesmo título rende mais de uma
                          linha: a categoria diz qual fatia é qual */}
                      <th style={{ width: '20%' }}>Categoria</th>
                      <th style={{ width: '14%' }}>Documento</th>
                      <th className="t-center" style={{ width: '13%' }}>Status</th>
                      <th className="t-end" style={{ width: '16%' }}>Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lanc.itens.map((l, i) => (
                      <tr key={i}>
                        <td className="pa-tab-num" style={{ whiteSpace: 'nowrap' }}>{fmtD(l.vencimento)}</td>
                        {dados?.consolidado && (
                          <td className="pa-text-muted" style={{ whiteSpace: 'nowrap' }}>{l.empresa || '—'}</td>
                        )}
                        <td className="t-strong">{l.nome || '—'}</td>
                        <td className="pa-text-muted">{l.categoria || '—'}</td>
                        <td className="pa-text-muted">{l.numero_documento || '—'}</td>
                        <td className="t-center"><span className="pa-chip">{l.status || '—'}</span></td>
                        <td className="t-end pa-tab-num t-strong">{formatarValor(l.valor)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="pa-modal-empty"><i className="fas fa-inbox"></i>Nenhum lançamento encontrado</div>
              )}
            </div>

            <div className="pa-modal-foot">
              <button type="button" className="pa-btn pa-btn-ghost" onClick={fecharModal}>Fechar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjetosAdvantec;
